/* eslint-disable react/react-in-jsx-scope */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useFetchUsers } from '../../../lib/query/userMapping';
import { TableName } from '../../../constants/table';
import { deleteEntity } from '../../../lib/services/api-service';

const commonColDef: Partial<GridColDef> = {
  flex: 1,
  headerClassName: 'gridHeader--header',
};

export const Columns = ({ tenantId }: { tenantId: string }): GridColDef[] => {
  const { refetch } = useFetchUsers({ tenantId });
  const [deleteParams, setDeleteParams] = useState<{
    PartitionKey: string;
    RowKey: string;
  }>({ PartitionKey: '', RowKey: '' });
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleDelete = (PartitionKey: string, RowKey: string) => {
    setDeleteParams({ PartitionKey, RowKey });
    setOpenConfirmation(true);
  };

  const handleClose = () => {
    setOpenConfirmation(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEntity(
        TableName.Users,
        deleteParams.PartitionKey,
        deleteParams.RowKey
      );
      toast.success(`Deleted User with E-mail: ${deleteParams.RowKey}`);
      refetch();
      setOpenConfirmation(false);
      window.location.reload(); // Refresh the page after successful deletion
    } catch (error) {
      toast.error('An error occurred while deleting user.');
    }
  };

  return [
    {
      field: 'tenantName',
      headerName: 'Tenant',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'Name',
      headerName: 'User Name',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'RowKey',
      headerName: 'Email',
      type: 'string',
      editable: false,
      ...commonColDef,
    },
    {
      field: 'role',
      headerName: 'Role',
      type: 'string',
      editable: true,
      ...commonColDef,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'gridHeader--header',
      cellClassName: 'actions',
      flex: 1,
      getActions: (params: GridRowParams) => {
        const { row } = params;
        const { PartitionKey, RowKey } = row;

        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            key={`delete-${RowKey}`}
            label='Delete'
            onClick={() => handleDelete(PartitionKey, RowKey)}
            color='inherit'
          />,
          <Dialog
            open={
              openConfirmation &&
              deleteParams.PartitionKey === PartitionKey &&
              deleteParams.RowKey === RowKey
            }
            onClose={handleClose}
            key={`confirmation-${RowKey}`}
          >
            <DialogContent>
              <Typography variant='body1'>
                Are you sure you want to remove this user?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleConfirmDelete} color='error'>
                Remove
              </Button>
            </DialogActions>
          </Dialog>,
        ];
      },
    },
  ];
};
